import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Stars, Badge, IconButton } from './UI';
import { fmt, imgUrl } from '../utils/api';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const Card = styled.div`
  background: #fff; border-radius: var(--radius-lg); overflow: hidden;
  box-shadow: var(--shadow-sm); border: 1px solid var(--gray-100);
  transition: all .3s ease; position: relative;
  &:hover { transform: translateY(-4px); box-shadow: var(--shadow-xl); }
`;
const ImgWrap = styled.div`
  position: relative; aspect-ratio: 4/3; overflow: hidden; background: var(--gray-100);
  img { width: 100%; height: 100%; object-fit: cover; transition: transform .5s ease; }
  ${Card}:hover & img { transform: scale(1.06); }
`;
const ActionBtns = styled.div`
  position: absolute; top: 10px; right: 10px;
  display: flex; flex-direction: column; gap: 6px;
  opacity: 0; transform: translateX(10px); transition: var(--transition);
  ${Card}:hover & { opacity: 1; transform: translateX(0); }
`;
const BadgePos = styled.div` position: absolute; top: 10px; left: 10px; `;
const Info = styled.div` padding: 14px; `;
const Name = styled.div`
  font-family: var(--font-display); font-size: 1rem; font-weight: 600;
  margin: 4px 0 8px; color: var(--gray-900);
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
`;
const PriceRow = styled.div` display: flex; align-items: center; gap: 8px; `;
const AddBtn = styled.button`
  width: 100%; padding: 9px; font-size: 0.85rem; font-weight: 500;
  background: var(--primary); color: #fff; border: none; cursor: pointer;
  transition: var(--transition); font-family: var(--font-body);
  &:hover { background: var(--primary-dark); }
`;

export default function ProductCard({ product, wishlistIds = [] }) {
  const { add } = useCart();
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const p = product;
  const price = p.sale_price || p.price;
  const discount = p.sale_price ? Math.round((1 - p.sale_price / p.price) * 100) : 0;
  const isWishlisted = wishlistIds.includes(p.id);

  const handleWishlist = async (e) => {
    e.preventDefault();
    if (!isLoggedIn) { navigate('/login'); return; }
    try {
      if (isWishlisted) { await api.delete(`/wishlist/${p.id}`); toast.success('Removed from wishlist'); }
      else { await api.post(`/wishlist/${p.id}`); toast.success('Added to wishlist'); }
    } catch { toast.error('Failed to update wishlist'); }
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    await add(p.id, null, 1, { name: p.name, price: p.price, sale_price: p.sale_price, image: p.primary_image });
  };

  return (
    <Card>
      <Link to={`/product/${p.slug}`}>
        <ImgWrap>
          <img src={imgUrl(p.primary_image)} alt={p.name} loading="lazy" />
          {discount > 0 && <BadgePos><Badge $color="error">-{discount}%</Badge></BadgePos>}
          {p.featured === 1 && !discount && <BadgePos><Badge $color="primary">Featured</Badge></BadgePos>}
          <ActionBtns onClick={e => e.preventDefault()}>
            <IconButton $size="sm" onClick={handleWishlist} title="Wishlist" style={isWishlisted ? { color:'var(--error)', borderColor:'var(--error)' } : {}}>
              {isWishlisted ? '♥' : '♡'}
            </IconButton>
            <IconButton $size="sm" onClick={handleAddToCart} title="Add to cart">🛒</IconButton>
          </ActionBtns>
        </ImgWrap>
      </Link>
      <Info>
        <div style={{ fontSize:'0.75rem', color:'var(--primary-light)', fontWeight:500, textTransform:'uppercase', letterSpacing:'0.5px' }}>{p.category_name}</div>
        <Link to={`/product/${p.slug}`}><Name>{p.name}</Name></Link>
        <div style={{ marginBottom:10 }}><Stars rating={p.avg_rating} count={p.review_count} /></div>
        <PriceRow>
          <span style={{ fontSize:'1.1rem', fontWeight:700, color:'var(--primary)' }}>{fmt.currency(price)}</span>
          {p.sale_price && <span style={{ fontSize:'0.85rem', color:'var(--gray-400)', textDecoration:'line-through' }}>{fmt.currency(p.price)}</span>}
        </PriceRow>
      </Info>
      <AddBtn onClick={handleAddToCart}>Add to Cart</AddBtn>
    </Card>
  );
}
